import type {
  AggregateOffer,
  UnitPriceSpecification,
} from "@decocms/apps-commerce/types";
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice";

/**
 * Sem juros = o total da parcela não passa do preço à vista. Mesmo critério de
 * `isNoInterest` em `~/utils/leanOffers.ts` (e de `installmentToString`, logo
 * abaixo). Não dá pra comparar `billingIncrement * billingDuration` com o
 * total: a VTEX arredonda o valor de cada parcela, então 386,10 em 4x vem como
 * 4x R$ 96,52 = R$ 386,08 (2 centavos a menos) e nenhuma parcela fecharia.
 */
const isNoInterest = (spec: UnitPriceSpecification, sellingPrice: number) =>
  spec.price <= sellingPrice + 0.01;

/**
 * Escolhe a parcela exibida no "ou Nx de R$ ...". Prioriza sem juros e, entre
 * as sem juros, o maior número de parcelas — é o que a loja quer anunciar.
 * Sem isso o PIX/boleto (sempre 1x e, quando tem desconto, de total menor)
 * ganhava da parcela sem juros do cartão e o card mostrava "1x" onde deveria
 * mostrar "4x".
 */
const bestInstallment =
  (sellingPrice: number) =>
  (acc: UnitPriceSpecification | null, curr: UnitPriceSpecification) => {
    if (curr.priceComponentType !== "https://schema.org/Installment") {
      return acc;
    }

    if (!acc) {
      return curr;
    }

    const accNoInterest = isNoInterest(acc, sellingPrice);
    const currNoInterest = isNoInterest(curr, sellingPrice);

    if (accNoInterest !== currNoInterest) {
      return currNoInterest ? curr : acc;
    }

    if (accNoInterest) {
      return (curr.billingDuration ?? 0) > (acc.billingDuration ?? 0)
        ? curr
        : acc;
    }

    if (acc.price > curr.price) {
      return curr;
    }

    if (acc.price < curr.price) {
      return acc;
    }

    if (
      acc.billingDuration &&
      curr.billingDuration &&
      acc.billingDuration < curr.billingDuration
    ) {
      return curr;
    }

    return acc;
  };

const installmentToString = (
  installment: UnitPriceSpecification,
  sellingPrice: number,
  showTaxesText?: boolean,
) => {
  const { billingDuration, billingIncrement, price } = installment;

  if (!billingDuration || !billingIncrement) {
    return "";
  }

  const withTaxes = sellingPrice < price;
  const taxesText = withTaxes ? "com juros" : "sem juros";

  // `billingIncrement.toFixed(2)` (versão anterior) sempre produz ponto
  // decimal ("R$ 124.75") -- `formatPrice` é o mesmo helper usado no resto
  // do site (preço principal, PDP, etc.) e já formata em pt-BR de verdade
  // (achado 06 da auditoria de 23/08/2026).
  return `${billingDuration}x de ${formatPrice(billingIncrement)} ${
    showTaxesText ? taxesText : ""
  }`;
};

export const useOffer = (
  aggregateOffer?: AggregateOffer,
  showTaxesText?: boolean,
) => {
  const offer = aggregateOffer?.offers[0];
  const listPrice = offer?.priceSpecification.find(
    (spec) => spec.priceType === "https://schema.org/ListPrice",
  );
  const seller = offer?.seller;
  const price = offer?.price;
  const installment = offer?.priceSpecification.reduce<
    UnitPriceSpecification | null
  >(bestInstallment(price ?? 0), null);
  const availability = offer?.availability;

  return {
    price,
    listPrice: listPrice?.price,
    availability,
    seller,
    installments: installment && price
      ? installmentToString(installment, price, showTaxesText)
      : null,
  };
};
